import {
  Field as ChakraField,
  NativeSelect,
  Text,
  VStack
} from "@chakra-ui/react"
import {
  AUGMENTATION_PANEL_INPUT_PROPS,
  AUGMENTATION_PANEL_SELECT_PROPS,
  AUGMENTATION_PANEL_STATIC_TEXT_PROPS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import { AugmentationComponent } from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import { AvatarImagePicker } from "@pathwright/ux/src/components/avatar/AvatarImagePicker"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Field from "@pathwright/ux/src/components/form/Field"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import MediaManager from "@pathwright/features/src/modules/media-manager/MediaManager"
import { Form, FormikProps, useFormikContext } from "formik"
import { useMemo, type MouseEventHandler } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  CoverPageContributorFragment as CoverPageContributor,
  CoverPageContributorFragmentDoc,
  useCreateCoverPageAuthorMutation,
  useUpdateCoverPageAuthorMutation
} from "@pathwright/graphql-client/src/generated"
import { useCoverPageContext } from "../CoverPageProvider"
import { COVER_PAGE_PANEL_ROOT_KEY } from "../constants"
import { getContributorPositioningContext } from "../positioning"
import { useCoverPageTranslate } from "../utils"
import { CoverPagePanelFormActions } from "./CoverPagePanelFormActions"
import { CoverPagePanelRichTextField } from "./CoverPagePanelRichTextField"
import { useCoverPagePanelErrorMessage } from "./utils"

const contributorSchema = z.object({
  avatar: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  salutation: z.string(),
  title: z.string(),
  bio: z.string()
})

type CoverPagePanelContributorValues = z.infer<typeof contributorSchema>

const salutationOptions = [
  "",
  "Mr.",
  "Mrs.",
  "Ms.",
  "Dr.",
  "Fr.",
  "Professor",
  "Rev.",
  "Rabbi"
]

function splitContributorName(name: string) {
  const nameParts = name.trim().split(/\s+/).filter(Boolean)

  if (nameParts.length <= 1) {
    return {
      first_name: nameParts[0] || "",
      last_name: ""
    }
  }

  return {
    first_name: nameParts.slice(0, -1).join(" "),
    last_name: nameParts[nameParts.length - 1] || ""
  }
}

function useContributorCreateName() {
  const location = useLocation()

  return useMemo(() => {
    return new URLSearchParams(location.search).get("name")?.trim() || ""
  }, [location.search])
}

function buildInitialValues(
  contributor: CoverPageContributor | null,
  initialName = ""
): CoverPagePanelContributorValues {
  const namesFromInitialName = contributor
    ? { first_name: "", last_name: "" }
    : splitContributorName(initialName)

  return {
    avatar: contributor?.avatar || contributor?.image || "",
    first_name: contributor?.first_name || namesFromInitialName.first_name,
    last_name: contributor?.last_name || namesFromInitialName.last_name,
    salutation: contributor?.salutation || "",
    title: contributor?.title || "",
    bio: contributor?.bio || ""
  }
}

function getContributorMutationVariables(
  values: CoverPagePanelContributorValues
) {
  return {
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    salutation: values.salutation,
    title: values.title.trim(),
    bio: values.bio || "",
    avatar: values.avatar || null
  }
}

export function preserveContributorPosition<
  T extends Pick<CoverPageContributor, "position">
>(updatedContributor: T, currentContributor: CoverPageContributor | null): T {
  if (updatedContributor.position || !currentContributor?.position) {
    return updatedContributor
  }

  return {
    ...updatedContributor,
    position: currentContributor.position
  }
}

function ContributorAvatarField({
  contextKey,
  isDisabled
}: {
  contextKey: string
  isDisabled: boolean
}) {
  const form = useFormikContext<CoverPagePanelContributorValues>()

  return (
    <VStack align="center" w="full">
      <MediaManager
        contextKey={contextKey}
        mediaType="image"
        pickOptions={{ maxFiles: 1 }}
        renderLoading={() => null}
        onChooseMedia={(media: { url: string }) => {
          void form.setFieldValue("avatar", media.url)
        }}
        renderButton={({
          onClick
        }: {
          onClick?: MouseEventHandler<HTMLElement>
        }) => (
          <AvatarImagePicker
            avatar={form.values.avatar}
            firstName={form.values.first_name}
            lastName={form.values.last_name}
            fullName={[form.values.first_name, form.values.last_name]
              .filter(Boolean)
              .join(" ")}
            isDisabled={isDisabled}
            onChange={(avatar) => {
              void form.setFieldValue("avatar", avatar)
            }}
            onPickAvatar={(event) => {
              if (!isDisabled) {
                onClick?.(event)
              }
            }}
          />
        )}
      />
    </VStack>
  )
}

function CoverPagePanelContributorForm({
  contributor
}: {
  contributor: CoverPageContributor | null
}) {
  const { t } = useCoverPageTranslate()
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const history = useHistory()
  const resource = useCoverPageContext((value) => value.resource)
  const contributors = useCoverPageContext((value) => value.contributors)
  const updateCoverPageResourceDetails = useCoverPageContext(
    (value) => value.updateCoverPageResourceDetails
  )
  const refetchCoverPageResource = useCoverPageContext(
    (value) => value.refetchCoverPageResource
  )

  const initialName = useContributorCreateName()

  const [createAuthorMutation] = useCreateCoverPageAuthorMutation()
  const [updateAuthorMutation] = useUpdateCoverPageAuthorMutation()
  const initialValues = useMemo(
    () => buildInitialValues(contributor, initialName),
    [contributor, initialName]
  )

  const isCreate = !contributor
  const formStateKey = useMemo(
    () => ["cover-page", "contributor", contributor?.id || "new", resource.id],
    [contributor?.id, resource.id]
  )

  const validate = useMemo(
    () =>
      toFormikValidate(
        contributorSchema.extend({
          first_name: z.string().refine(
            (value) => value.trim().length > 0,
            t("panel_contributors.first_name_required", {
              defaultValue: "Enter a contributor first name."
            })
          ),
          last_name: z.string().refine(
            (value) => value.trim().length > 0,
            t("panel_contributors.last_name_required", {
              defaultValue: "Enter a contributor last name."
            })
          )
        })
      ),
    [t]
  )

  async function handleSubmit(values: CoverPagePanelContributorValues) {
    try {
      const variables = getContributorMutationVariables(values)
      const contributorPositioningContext = getContributorPositioningContext(
        resource.id
      )
      const result = isCreate
        ? await createAuthorMutation({
            variables: {
              ...variables,
              avatar: variables.avatar || undefined,
              contributorPositioningContext,
              order: 0
            }
          })
        : await updateAuthorMutation({
            variables: {
              ...variables,
              avatar: variables.avatar || undefined,
              contributorPositioningContext,
              id: contributor!.id
            },
            update: (cache, { data }) => {
              const updatedContributor = data?.updateAuthor
              const cacheId = updatedContributor
                ? cache.identify(updatedContributor)
                : null

              if (!updatedContributor || !cacheId) return

              cache.writeFragment({
                id: cacheId,
                fragment: CoverPageContributorFragmentDoc,
                fragmentName: "CoverPageContributor",
                variables: {
                  contributorPositioningContext
                },
                data: preserveContributorPosition(
                  updatedContributor,
                  contributor
                )
              })
            }
          })
      const savedContributor =
        result.data && "createAuthor" in result.data
          ? result.data.createAuthor
          : result.data && "updateAuthor" in result.data
          ? result.data.updateAuthor
          : null
      const contributorId = savedContributor?.id || contributor?.id

      if (!contributorId) {
        throw new Error(
          t("panel_contributors.unable_to_save_contributor", {
            defaultValue: "Unable to save contributor."
          })
        )
      }

      if (isCreate) {
        await updateCoverPageResourceDetails({
          authors: Array.from(
            new Set(
              contributors
                .map((currentContributor) => currentContributor.id)
                .concat(contributorId)
            )
          )
        })
      }

      await refetchCoverPageResource()

      if (isCreate) {
        history.push(`/${COVER_PAGE_PANEL_ROOT_KEY}`)
      }
    } catch (error) {
      toaster.create({
        title: getErrorMessage(
          error,
          isCreate
            ? t("panel_errors.create_contributor", {
                defaultValue: "Error creating Cover Page's contributor:"
              })
            : t("panel_errors.update_contributor", {
                defaultValue: "Error updating Cover Page's contributor:"
              })
        ),
        type: "error",
        duration: 4000,
        closable: true
      })
      throw error
    }
  }

  return (
    <FormikWithDiscardFormState
      formStateKey={formStateKey}
      initialValues={initialValues}
      validate={validate}
      validateOnMount
      enableReinitialize
      formStateOnSubmit={isCreate ? "clearOnSuccess" : "preserve"}
      onSubmit={handleSubmit}
    >
      {(form: FormikProps<CoverPagePanelContributorValues>) => {
        const formId = "cover-page-panel-contributor"

        return (
          <Form
            id={formId}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%"
            }}
          >
            <KeyboardFormSubmit />
            <VStack align="stretch" gap="15px" w="full" pt="15px">
              <ContributorAvatarField
                contextKey={`resource-${resource.id}`}
                isDisabled={form.isSubmitting}
              />
              <Field
                {...AUGMENTATION_PANEL_INPUT_PROPS}
                name="first_name"
                label={t("panel_contributors.first_name", {
                  defaultValue: "First name"
                })}
                placeholder={t("panel_contributors.first_name_placeholder", {
                  defaultValue: "Enter contributor's first name"
                })}
              />
              <Field
                {...AUGMENTATION_PANEL_INPUT_PROPS}
                name="last_name"
                label={t("panel_contributors.last_name", {
                  defaultValue: "Last name"
                })}
                placeholder={t("panel_contributors.last_name_placeholder", {
                  defaultValue: "Enter contributor's last name"
                })}
              />
              <ChakraField.Root>
                <ChakraField.Label
                  mb={1}
                  {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}
                >
                  {t("panel_contributors.salutation", {
                    defaultValue: "Salutation"
                  })}
                </ChakraField.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    {...AUGMENTATION_PANEL_SELECT_PROPS}
                    name="salutation"
                    value={form.values.salutation}
                    onChange={form.handleChange}
                    {...({ disabled: form.isSubmitting } as any)}
                  >
                    {salutationOptions.map((salutation) => (
                      <option key={salutation || "none"} value={salutation}>
                        {salutation ||
                          t("panel_contributors.no_salutation", {
                            defaultValue: "None"
                          })}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </ChakraField.Root>
              <Field
                {...AUGMENTATION_PANEL_INPUT_PROPS}
                name="title"
                label={t("panel_contributors.title", {
                  defaultValue: "Title"
                })}
                placeholder={t("panel_contributors.title_placeholder", {
                  defaultValue: "e.g. Software Engineer at Google"
                })}
              />
              <CoverPagePanelRichTextField<CoverPagePanelContributorValues>
                name="bio"
                label={t("panel_contributors.bio", {
                  defaultValue: "Bio"
                })}
                placeholder={t("panel_contributors.bio_placeholder", {
                  defaultValue: "About the contributor"
                })}
                disabled={form.isSubmitting}
              />
            </VStack>
            <CoverPagePanelFormActions
              form={form}
              formId={formId}
              requireDirty={!isCreate}
              submit={{
                children: isCreate
                  ? t("panel_contributors.create_contributor", {
                      defaultValue: "Create contributor"
                    })
                  : t("panel.apply_changes", {
                      defaultValue: "Apply changes"
                    })
              }}
              discard={{
                children: isCreate
                  ? t("panel.discard", { defaultValue: "Discard" })
                  : t("panel.discard_changes", {
                      defaultValue: "Discard changes"
                    })
              }}
            />
          </Form>
        )
      }}
    </FormikWithDiscardFormState>
  )
}

export function CoverPagePanelContributorCreateRoute() {
  return <CoverPagePanelContributorForm contributor={null} />
}

export const CoverPagePanelContributorRoute: AugmentationComponent = ({
  route
}) => {
  const { t } = useCoverPageTranslate()
  const contributors = useCoverPageContext((value) => value.contributors)
  const contributorId = Number(
    (route?.match?.params as { contributorId?: string })?.contributorId || ""
  )
  const contributor =
    contributors.find((contributor) => contributor.id === contributorId) || null

  if (!contributor) {
    return (
      <Text fontSize="sm" color="blackAlpha.700" m={0}>
        {t("panel_contributors.contributor_not_found", {
          defaultValue: "Contributor not found."
        })}
      </Text>
    )
  }

  return <CoverPagePanelContributorForm contributor={contributor} />
}
